import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from './inboxIcons';

// Files attached in the reply box. Each file uploads as soon as it's added (so Send is quick),
// shows a preview, and is sent by id with the reply. The server checks everything again.
const MB = 1024 * 1024;
const TYPES = { 'image/jpeg': 'image', 'image/png': 'image', 'image/webp': 'image', 'application/pdf': 'document', 'video/mp4': 'video' };
export const ACCEPT = Object.keys(TYPES).join(',');
export const MAX_FILES = 5;
// Same limits as the server (backend/services/supportFiles.js)
const LIMITS = {
  email: { image: 18 * MB, document: 18 * MB, video: 18 * MB, total: 18 * MB },
  whatsapp: { image: 5 * MB, document: 25 * MB, video: 16 * MB },
  messenger: { image: 8 * MB, document: 25 * MB, video: 25 * MB },
  instagram: { image: 8 * MB, document: null, video: 25 * MB },
  note: { image: 25 * MB, document: 25 * MB, video: 25 * MB },
};
const CH = { email: 'Email', whatsapp: 'WhatsApp', messenger: 'Messenger', instagram: 'Instagram', note: 'notes' };
const KIND = { image: 'Images', document: 'PDFs', video: 'Videos' };
export const fileSize = (n) => (n >= MB ? `${Math.round((n / MB) * 10) / 10} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Why these files can't go on this channel, or null. */
export function channelProblem(channel, files) {
  const lim = LIMITS[channel];
  if (!lim) return files.length ? 'Files can only be sent by email, WhatsApp, Instagram, Messenger or in a note.' : null;
  for (const f of files) {
    const max = lim[f.kind];
    if (max === null || max === undefined) return `${KIND[f.kind]} can't be sent on ${CH[channel]}. Remove ${f.name}.`;
    if (f.size > max) return `${f.name} is ${fileSize(f.size)}. ${CH[channel]} allows ${KIND[f.kind].toLowerCase()} up to ${fileSize(max)}.`;
  }
  const total = files.reduce((s, f) => s + f.size, 0);
  if (lim.total && total > lim.total) return `Attachments add up to ${fileSize(total)}. ${CH[channel]} allows ${fileSize(lim.total)} in total.`;
  return null;
}

/** Per-conversation attachment state + upload. */
export function useReplyFiles({ apiBase, getToken }) {
  const [byId, setById] = useState({}); // conversation id → files
  const urls = useRef(new Set());
  useEffect(() => () => urls.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const update = (convId, key, patch) => setById((p) => ({ ...p, [convId]: (p[convId] || []).map((f) => (f.key === key ? { ...f, ...patch } : f)) }));

  const upload = useCallback(async (convId, f, file) => {
    try {
      const token = await getToken();
      const res = await fetch(`${apiBase}/api/v1/support/uploads`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name || 'file') },
        body: file,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.message || `Upload failed (${res.status})`);
      update(convId, f.key, { status: 'ready', uploadId: data.upload.uploadId, name: data.upload.filename });
    } catch (err) {
      update(convId, f.key, { status: 'error', error: err.message });
    }
  }, [apiBase, getToken]);

  /** Add files from the picker, a drop or a paste. Returns a message when some were refused. */
  const add = useCallback((convId, fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return null;
    let refused = null;
    const current = byId[convId] || [];
    const room = MAX_FILES - current.length;
    if (room <= 0) return `Attach at most ${MAX_FILES} files to one message.`;
    const accepted = [];
    incoming.forEach((file) => {
      const kind = TYPES[file.type];
      if (!kind) { refused = `${file.name || 'That file'} can't be sent. Use JPG, PNG, WEBP, PDF or MP4.`; return; }
      if (file.size > 25 * MB) { refused = `${file.name} is ${fileSize(file.size)}. The most we can send is 25 MB.`; return; }
      if (accepted.length >= room) { refused = `Attach at most ${MAX_FILES} files to one message.`; return; }
      const preview = kind === 'image' ? URL.createObjectURL(file) : null;
      if (preview) urls.current.add(preview);
      accepted.push({ f: { key: `${Date.now()}-${Math.random().toString(36).slice(2)}`, name: file.name || 'file', size: file.size, kind, preview, status: 'uploading' }, file });
    });
    if (accepted.length) {
      setById((p) => ({ ...p, [convId]: [...(p[convId] || []), ...accepted.map((a) => a.f)] }));
      accepted.forEach((a) => upload(convId, a.f, a.file));
    }
    return refused;
  }, [byId, upload]);

  const remove = useCallback((convId, key) => setById((p) => ({ ...p, [convId]: (p[convId] || []).filter((f) => f.key !== key) })), []);
  const clear = useCallback((convId) => setById((p) => ({ ...p, [convId]: [] })), []);
  const restore = useCallback((convId, files) => setById((p) => ({ ...p, [convId]: [...files, ...(p[convId] || [])].slice(0, MAX_FILES) })), []);
  return { byId, add, remove, clear, restore };
}

/** The row of attached files under the reply box. */
export function FileChips({ files, onRemove }) {
  if (!files?.length) return null;
  return (
    <ul className="cx-files" aria-label="Attached files">
      {files.map((f) => (
        <li key={f.key} className={`cx-file${f.status === 'error' ? ' err' : ''}`} title={f.error || f.name}>
          {f.preview ? <img src={f.preview} alt="" /> : <span className="cx-file-ico"><Icon name="attach" size={15} /></span>}
          <span className="cx-file-txt">
            <span className="cx-file-name">{f.name}</span>
            <span className="cx-file-meta">{f.status === 'uploading' ? 'Uploading…' : f.status === 'error' ? f.error : fileSize(f.size)}</span>
          </span>
          <button className="cx-file-x" onClick={() => onRemove(f.key)} aria-label={`Remove ${f.name}`}>×</button>
        </li>
      ))}
    </ul>
  );
}

/** Thumbnails on a reply that is still sending. */
export function PendingFiles({ files }) {
  if (!files?.length) return null;
  return (
    <div className="cx-att-row">
      {files.map((f) => (f.preview
        ? <img key={f.key} className="cx-pending-img" src={f.preview} alt={f.name} />
        : <span key={f.key} className="cx-attach"><Icon name="attach" size={14} />{f.name}</span>))}
    </div>
  );
}
